/**
 * ArtifactManager — copies test artifacts to structured output folders.
 *
 * Handles screenshot/video files from Playwright's result.attachments,
 * organizing them into per-test folders with sanitized names.
 *
 * Uses async copies with a concurrency limiter to avoid blocking the
 * event loop and exhausting file descriptors on large suites.
 */

import { mkdirSync, existsSync } from 'node:fs';
import { copyFile } from 'node:fs/promises';
import { join, extname } from 'node:path';
import type { TestArtifacts } from '@testrelic/core';

interface Attachment {
  name: string;
  contentType: string;
  path?: string;
  body?: Buffer;
}

// ---------------------------------------------------------------------------
// Async copy queue with concurrency limiter
// ---------------------------------------------------------------------------

const COPY_CONCURRENCY = 20;
let activeCopies = 0;
const pendingCopies: Array<() => void> = [];

/** Queue a file copy with bounded concurrency to prevent fd exhaustion. */
async function enqueueCopy(src: string, dest: string): Promise<boolean> {
  if (activeCopies >= COPY_CONCURRENCY) {
    await new Promise<void>(resolve => pendingCopies.push(resolve));
  }
  activeCopies++;
  try {
    await copyFile(src, dest);
    return true;
  } catch {
    return false;
  } finally {
    activeCopies--;
    if (pendingCopies.length > 0) pendingCopies.shift()!();
  }
}

// Tracks all in-flight copy promises so onEnd can await them
const inflightCopies: Promise<void>[] = [];

/** Wait for all queued artifact copies to complete. Call from reporter onEnd. */
export async function flushPendingArtifactCopies(): Promise<void> {
  if (inflightCopies.length === 0) return;
  await Promise.allSettled(inflightCopies);
  inflightCopies.length = 0;
}

// ---------------------------------------------------------------------------
// Timestamp folder naming
// ---------------------------------------------------------------------------

/**
 * Generate a timestamp-based folder name for the current test run.
 * Format: YYYY-MM-DDTHH-mm-ss (filesystem-safe, sorts chronologically).
 *
 * If the target directory already exists (sub-second collision),
 * appends a numeric suffix: `-1`, `-2`, etc.
 */
export function generateTimestampFolderName(artifactsDir: string): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  const base = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

  if (!existsSync(join(artifactsDir, base))) {
    return base;
  }

  // Collision: append numeric suffix
  let suffix = 1;
  while (existsSync(join(artifactsDir, `${base}-${suffix}`))) {
    suffix++;
  }
  return `${base}-${suffix}`;
}

// ---------------------------------------------------------------------------
// Folder name sanitization
// ---------------------------------------------------------------------------

/**
 * Sanitize a test title into a filesystem-safe folder name.
 *
 * Rules:
 * 1. Replace characters not in [a-zA-Z0-9-_ ] with hyphens
 * 2. Replace spaces with hyphens
 * 3. Collapse consecutive hyphens
 * 4. Trim leading/trailing hyphens
 * 5. Truncate to 100 characters
 */
export function sanitizeFolderName(title: string): string {
  let name = title
    .replace(/[^a-zA-Z0-9\-_ ]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  if (name.length > 100) {
    name = name.substring(0, 100).replace(/-+$/, '');
  }

  return name || 'unnamed-test';
}

/** Characters of the testId kept as the folder-name disambiguator. */
const ID_SUFFIX_LENGTH = 8;

/**
 * Build the per-test artifact folder name.
 *
 * The sanitized leaf title alone is NOT unique: the same test title can appear
 * in several spec files, in several `describe` blocks, and once per Playwright
 * project (chromium/firefox/webkit). All of those would sanitize to the same
 * folder and race each other writing `video.webm` / `screenshot.png`, leaving
 * every colliding test pointing at one file — so distinct testIds ended up
 * uploading byte-identical artifacts.
 *
 * `testId` (the reporter's stable sha256 of filePath::suiteName::fullTitle)
 * disambiguates them. It is appended as a short suffix so the folder stays
 * human-readable while being unique per test.
 *
 * @param testTitle  Test title used for the readable part of the folder name.
 * @param retryCount Playwright retry index (0 for the first attempt).
 * @param testId     Stable per-test identifier. When omitted the legacy
 *                   title-only naming is used (kept for callers that have no id).
 */
export function buildArtifactFolderName(
  testTitle: string,
  retryCount: number,
  testId?: string,
): string {
  let folderName = sanitizeFolderName(testTitle);

  const suffix = testId ? sanitizeFolderName(testId).substring(0, ID_SUFFIX_LENGTH) : '';
  if (suffix) {
    folderName += `--${suffix}`;
  }

  if (retryCount > 0) {
    folderName += `--retry-${retryCount}`;
  }

  return folderName;
}

// ---------------------------------------------------------------------------
// Artifact copy (non-blocking)
// ---------------------------------------------------------------------------

/**
 * Queue artifact copies from Playwright's temp locations to structured output folders.
 * Returns paths immediately (optimistic) — copies happen asynchronously.
 * Call `flushPendingArtifactCopies()` in onEnd to ensure all copies complete.
 *
 * @param testId Stable per-test id used to disambiguate the destination folder
 *               when two tests share a leaf title (different spec file,
 *               different describe block, or different Playwright project).
 *               Without it those tests collide on one folder and overwrite each
 *               other's `video.webm` / `screenshot.png`.
 * @returns TestArtifacts with relative paths, or null if no artifacts found.
 */
export function copyArtifacts(
  attachments: readonly Attachment[],
  testTitle: string,
  retryCount: number,
  outputDir: string,
  runTimestamp?: string,
  testId?: string,
): TestArtifacts | null {
  const screenshot = attachments.find(
    (a) => a.name === 'screenshot' && a.path,
  );
  const video = attachments.find(
    (a) => a.name === 'video' && a.path,
  );

  if (!screenshot && !video) {
    return null;
  }

  const folderName = buildArtifactFolderName(testTitle, retryCount, testId);

  // When runTimestamp is provided, nest under timestamp folder:
  //   artifacts/<runTimestamp>/<folderName>/
  // Otherwise fall back to legacy flat structure:
  //   artifacts/<folderName>/
  const artifactSegments = runTimestamp
    ? ['artifacts', runTimestamp, folderName]
    : ['artifacts', folderName];
  const artifactDir = join(outputDir, ...artifactSegments);
  const relativeParts = artifactSegments;
  const result: { screenshot?: string; video?: string } = {};

  try {
    mkdirSync(artifactDir, { recursive: true });
  } catch {
    // Cannot create directory — skip artifact capture
    return null;
  }

  if (screenshot?.path) {
    if (existsSync(screenshot.path)) {
      const ext = extname(screenshot.path) || '.png';
      const destName = `screenshot${ext}`;
      const dest = join(artifactDir, destName);
      // Fire-and-forget async copy; tracked for flush in onEnd
      inflightCopies.push(enqueueCopy(screenshot.path, dest).then(() => {}));
      result.screenshot = `${relativeParts.join('/')}/${destName}`;
    }
  }

  if (video?.path) {
    if (existsSync(video.path)) {
      const ext = extname(video.path) || '.webm';
      const destName = `video${ext}`;
      const dest = join(artifactDir, destName);
      inflightCopies.push(enqueueCopy(video.path, dest).then(() => {}));
      result.video = `${relativeParts.join('/')}/${destName}`;
    }
  }

  if (!result.screenshot && !result.video) {
    return null;
  }

  return result;
}
